
import React from 'react';
import { TicketType } from '../types';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Save } from "lucide-react";

interface TicketFormProps {
  editingTicket: TicketType;
  onSave: () => void;
  onCancel: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSwitchChange: (checked: boolean) => void;
  saving: boolean;
}

const TicketForm: React.FC<TicketFormProps> = ({
  editingTicket,
  onSave,
  onCancel,
  onInputChange,
  onSwitchChange,
  saving
}) => {
  return (
    <Card className="mb-8 bg-white/5 border-purple-500/20 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-white">
          {editingTicket.id ? 'Edit Ticket Type' : 'Create New Ticket Type'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium text-white">
              Ticket Name
            </label>
            <Input
              id="name"
              name="name"
              value={editingTicket.name}
              onChange={onInputChange}
              placeholder="Early Bird, VIP, etc."
              required
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="active" className="text-sm font-medium text-white">
                Active
              </Label>
              <Switch
                id="active"
                checked={editingTicket.active}
                onCheckedChange={onSwitchChange}
              />
            </div>
            <p className="text-xs text-gray-400">
              Toggle to enable or disable ticket sales
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="description" className="text-sm font-medium text-white">
            Description
          </label>
          <Textarea
            id="description"
            name="description"
            value={editingTicket.description}
            onChange={onInputChange}
            placeholder="Ticket description"
            rows={2}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label htmlFor="price" className="text-sm font-medium text-white">
              Price
            </label>
            <Input
              id="price"
              name="price"
              type="number"
              min="0"
              step="0.01"
              value={editingTicket.price}
              onChange={onInputChange}
              placeholder="99.99"
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="currency" className="text-sm font-medium text-white">
              Currency
            </label>
            <Input
              id="currency"
              name="currency"
              value={editingTicket.currency}
              onChange={onInputChange}
              placeholder="USD"
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="quantity" className="text-sm font-medium text-white">
              Quantity (optional)
            </label>
            <Input
              id="quantity"
              name="quantity"
              type="number"
              min="0"
              value={editingTicket.quantity === null ? '' : editingTicket.quantity}
              onChange={onInputChange}
              placeholder="Leave empty for unlimited"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="available_from" className="text-sm font-medium text-white">
              Available From
            </label>
            <Input
              id="available_from"
              name="available_from"
              type="date"
              value={editingTicket.available_from}
              onChange={onInputChange}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="available_until" className="text-sm font-medium text-white">
              Available Until
            </label>
            <Input
              id="available_until"
              name="available_until"
              type="date"
              value={editingTicket.available_until}
              onChange={onInputChange}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="features" className="text-sm font-medium text-white">
            Features (one per line)
          </label>
          <Textarea
            id="features"
            name="features"
            value={editingTicket.features}
            onChange={onInputChange}
            placeholder="Access to all sessions&#10;Lunch included&#10;etc."
            rows={4}
          />
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button 
          variant="outline"
          onClick={onCancel}
        >
          <X className="mr-2 h-4 w-4" /> Cancel
        </Button>
        <Button 
          onClick={onSave}
          className="bg-purple-600 hover:bg-purple-700"
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" /> Save Ticket
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default TicketForm;
